import { Flex, Progress, Spin } from "antd";
import {
  useEffect,
  useMemo,
  useState
} from "react";
import { useNavigate, useParams } from "react-router-dom";

import { API_PATHS } from "../../api";
import { PATHS } from "../../constants/PATHS";
import { useSignalR } from "../../signalR";
import { Description } from "../Description";
import { Title } from "../Title";

export const PlannerImportProgress = () => {
  const [messages, setMessages] = useState<any[]>([]);
  const [stats, setStats] = useState<any>({});
  const [jobStatus, setJobStatus] = useState<"normal" | "active" | "exception" | "success" | "interrupted" | undefined>("normal");

  const [error, setError] = useState<any | null>(null);
  const navigate = useNavigate();
  const { operationIdentifier, instanceIdentifier } = useParams();

  const addMessage = (msg: string) => {
    setMessages((prevMessages) => [...prevMessages, msg]);
  };

  const connection = useSignalR(
    `${API_PATHS.BASE_URLS.API_URL}${API_PATHS.HUBS.SIGNALR_PROGRESS()}`,
    [
      ["Register", (opId) => {
        setJobStatus("active");
      }],
      ["Notification", (msg) => {
        const { totalFetched, totalSaved, elapsedSeconds, entityIndex, totalEntitiesToFetch } = msg;
        const message = `${msg.entityName}`;
        addMessage(message);

        if (msg.status === "Done" && msg.entityName === "All") {
          setJobStatus("success");
          navigate(`${PATHS.MSPLANNER}/${instanceIdentifier}/${operationIdentifier}/success`);
        }
        setStats({ totalFetched, totalSaved, elapsedSeconds, entityIndex, totalEntitiesToFetch });
      }],
      ["Error", (msg) => {
        setError(JSON.parse(msg.content));
        setJobStatus("exception");
      }]
    ],
    [operationIdentifier]
  );

  useEffect(() => {
    if (!connection) return;

    connection.start()
      .then(() => connection.invoke("Register", operationIdentifier))
      .catch(err => {
        console.error("Connection error: ", err)
        setJobStatus("exception");
      });

    return () => {
      console.log("Stopping connection");
      setJobStatus("interrupted");
      connection.stop();
    };
  }, [connection]);

  const content = useMemo(() => {
    if (jobStatus === "success") {
      return <Description theme="t3">Import completed successfully!</Description>;
    }
    return (
      <>
        {error && <ErrorMessage error={error} />}
        
        <Message messages={messages.slice(-5)} stats={stats} />
      </>
    );
  }, [error, messages, stats]);

  const percent = stats.totalEntitiesToFetch ? stats?.entityIndex / stats?.totalEntitiesToFetch * 100 : 0;

  return (
    <Flex vertical align="center" justify="flex-start" gap={36} style={{ width: "100%" }}>
      <Title tagName="h2">Import in progress</Title>
      {!error && <Spin size="large" />}
      {jobStatus === "active" && <Progress
        percent={percent}
        showInfo={false}
        status={jobStatus}
        style={{ width: "75%" }}
      />}
      {content}      
    </Flex> 
  );
};

function ErrorMessage({ error }: { error: any }) {
  return (
    <div>
      <Title tagName="h3">Error: {error.StatusCode}</Title>
      <Description theme="t5">
        <p>{error.ExceptionMessage}</p>
        <p>{error.SupportMessage}</p>
        <p>{error.Source}</p>
      </Description>
      <Description theme="t6">{error.ErrorId}</Description>
    </div>
  );
}

function Message({ messages, stats }: { messages: any[]; stats: any }) {
  return (
    <Description theme="t4">
      {/* Total: {stats.totalFetched || 0} fetched, {stats.totalSaved || 0} saved, elapsed seconds: {stats.elapsedSeconds || 0} */}
      {messages.map((msg) => <p>{msg}</p>)}
    </Description>
  );
}